import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { CurrentProjectContext, readStoredProjectId, storeProjectId } from './currentProject';

/**
 * Remembers which project the person is working in, across pages and reloads.
 *
 * @remarks
 * Every page with a project selector used to keep its own `useState('')` and fall back to the
 * first project in the list. So choosing a project on the board and then opening the Gantt
 * showed a different project, and the second one looked like the answer rather than a
 * default - which is how somebody reads a plan for work that is not theirs.
 *
 * **This stores an id, never a permission.** The list of projects on every page comes from the
 * server, scoped to what GitLab grants; this only points at one of them. A remembered id the
 * person can no longer see is ignored rather than honoured - see `useCurrentProject` - so
 * nothing here can widen what anybody sees.
 */
export function CurrentProjectProvider({ children }: { children: ReactNode }) {
  // Read once on mount rather than on every render: the value only changes through `remember`,
  // and reading storage during render would make the component's output depend on something
  // React cannot see.
  const [rememberedProjectId, setRemembered] = useState(readStoredProjectId);

  const remember = useCallback((projectId: string) => {
    setRemembered(projectId);
    storeProjectId(projectId);
  }, []);

  const value = useMemo(
    () => ({ rememberedProjectId, remember }),
    [rememberedProjectId, remember],
  );

  return (
    <CurrentProjectContext.Provider value={value}>{children}</CurrentProjectContext.Provider>
  );
}
